import axios from "axios";
import React, {useState, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {imagesBlobState, secondaryModals, selectedShippingAddress, imageFrameState, giftCheckoutState} from "@atoms";
import {
  netPriceState,
  imageCountState,
  framePricesState,
  defaultFramePriceState,
  handlingFeeState,
  shippingPriceState,
  creditsAppliedState
} from "@atoms/priceCalc";
import {OrderStatus} from "../../../constants/enums/orderStatus";
import AmountCharged from "../../../helpers/AmountCharged";
import {rememberOrderAccess} from "../../../helpers/utils/orderAccess";
import {validCoupon} from "../../../Recoil/Atoms";
import "./style.css";
import {checkOrderStatus} from "../../../api/requests";
import useGooglePay from "../../../helpers/hooks/useGooglePay";

const BASE_URL = process.env.REACT_APP_BASE_URL;
// Frame key -> Hebrew label, for the itemized invoice line (pdesc).
const GPAY_FRAME_LABELS = { classic: "קלאסי", bold: "נועז", ever: "נקי", clean: "אותנטי", village: "כפרי", nordic: "נורדי" };

const SelectCardModal = ({style}) => {
  const setModals = useSetRecoilState(secondaryModals);
  const selectedAddress = useRecoilValue(selectedShippingAddress);
  const frameChoose = useRecoilValue(imageFrameState);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Google Pay (Tranzila Hosted Fields) — shown alongside Bit/card on desktop.
  const { googlePayEligible, chargeGooglePay } = useGooglePay();
  const [gpayOrderId, setGpayOrderId] = useState(null);
  const [gpayThtk, setGpayThtk] = useState(null);

  const imageCount = useRecoilValue(imageCountState);
  const imagesBlob = useRecoilValue(imagesBlobState);
  const [numberOfImages] = useState(5);
  const netPrice = useRecoilValue(netPriceState);
  const selectedCoupon = useRecoilValue(validCoupon);
  const [framePrices] = useRecoilState(framePricesState);
  const [defaultFramePrice] = useRecoilState(defaultFramePriceState);
  const handlingFee = useRecoilValue(handlingFeeState);
  // Configured shipping (/api/shipping, loaded in App) — the same price the drawer charges.
  const shippingPrice = useRecoilValue(shippingPriceState);
  // Points the checkout drawer applied to this cart. The payment webhook spends
  // cart.creditsApplied after the charge, so the charge must already include them.
  const creditsApplied = useRecoilValue(creditsAppliedState);

  // Get the current frame type from user's choice or fallback to localStorage
  const currentFrameType = frameChoose || localStorage.getItem("frameSelected") || "classic";

  // Get the price for the current frame type, or fall back to the default price
  const framePrice = framePrices[currentFrameType] || defaultFramePrice;

  // GIFT-CARD mode: charge the server-priced gift amount against the GC- order
  // (pre-created — no checkorder / createorder here).
  const giftCheckout = useRecoilValue(giftCheckoutState);
  const giftMode = !!giftCheckout;

  const amountCharged = giftMode ? giftCheckout.amount : AmountCharged(imageCount + imagesBlob.length, numberOfImages,
    netPrice + (imagesBlob.length * framePrice), shippingPrice, selectedCoupon, handlingFee, creditsApplied);

  // The card form charges + records the order itself (one press) — no verify-first mode.
  const handleOpenNewCardForm = () => {
    setModals(md => ({
      ...md,
      selectCard: {visible: false},
      addCard: {visible: true},
    }));
  };

  const creatOrder = async (data) => {
    try {
      rememberOrderAccess(); // the success page needs these after localStorage.clear()
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      const res = await axios.post(`${BASE_URL}/gama-createorder`, { ...data, sessionId: localStorage.getItem("sessionId") }, config);
      if (res.data.status === 200) {
        console.log("----success-----");
      }
      return res.data;
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  const handleBitPayment = async () => {
    setIsLoading(true);

    let haveToCreate = true;
    let gamaAuthResponse;
    const gamaAccessToken = localStorage.getItem("gamaAccessToken");
    const expiresAt = localStorage.getItem("gamaAccessTokenExpiresAt");

    try {
      if (giftMode) {
        haveToCreate = false; // GC- order already exists server-side
      } else if (localStorage.getItem("orderId")) {
        const checkResp = await checkOrderStatus();
        if (checkResp.data && checkResp.data.orderStatus === OrderStatus.NOT_PAID) {
          haveToCreate = false;
        } else {
          localStorage.removeItem("orderId");
          throw new Error("Order is already paid or failed");
        }
      }

      if (!gamaAccessToken || expiresAt <= Date.now()) {
        gamaAuthResponse = await axios.post(`${BASE_URL}/gama-login`);

        if (gamaAuthResponse.data.status !== 200) {
          throw new Error("Gama auth failed.");
        }

        localStorage.setItem("gamaAccessToken", gamaAuthResponse.data.access_token);
        localStorage.setItem("gamaAccessTokenExpiresAt", (Date.now() + gamaAuthResponse.data.expires_in));
      }

      const gamaSessionResponse = await axios.post(`${BASE_URL}/gama-session`, {
        gamaAccessToken: localStorage.getItem("gamaAccessToken"),
      });

      if (gamaSessionResponse.data.status !== 200) {
        throw new Error("Gama session creating failed.");
      }

      if (haveToCreate) {
        // Get the current frame selection from user's choice or fallback to localStorage
        const currentFrameType = frameChoose || localStorage.getItem("frameSelected");
        
        const orderData = await creatOrder(
          {
            ...selectedAddress,
            uid: localStorage.getItem("uniqueUserId"),
            finalAmount: amountCharged,
            orderStatus: OrderStatus.NOT_PAID,
            imagesPayed: imageCount + imagesBlob.length,
            frameType: currentFrameType // Add the frame type to the order data
          });

        if (!orderData) {
          throw new Error("Creating order failed.");
        }

        localStorage.setItem("orderId", orderData.oid);
      }

      // Open the Bit modal only once localStorage holds the orderId: on mount it
      // subscribes to (and fast-polls) the order in localStorage. Opened before
      // creatOrder (as it was), it subscribed to nothing — a 5 s fallback poll saw
      // PAID, stopped, and no one navigated (prod order 534417154, 2026-10-02).
      setModals(state => ({
        ...state,
        selectCard: {visible: false},
        bitPay: {visible: true, data: amountCharged},
      }));

      const id = localStorage.getItem("orderId");

      const createTransactionResponse = await axios.post(`${BASE_URL}/gama-create-payment`, {
        orderId: id,
        amount: amountCharged,
        payerName: (giftMode ? giftCheckout.buyerName : selectedAddress.fullName) || "Blends",
        payerPhoneNumber: giftMode ? giftCheckout.buyerPhone : selectedAddress.phoneNumber,
        transactionId: gamaSessionResponse.data.transactionProcessingId,
        gamaAccessToken: localStorage.getItem("gamaAccessToken"),
        gamaSessionToken: gamaSessionResponse.data.token,
        transactionEndpoint: gamaSessionResponse.data.transactionProcessingEndpoint,
      });

      if (createTransactionResponse.data.status !== 200) {
        throw new Error("Gama transaction creating failed.");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Preload order + handshake token (thtk) for Google Pay once eligible, so the tap
  // fires chargeGpay synchronously (Google Pay requires the user gesture).
  useEffect(() => {
    const preload = async () => {
      if (!googlePayEligible || (!giftMode && !selectedAddress?.email) || gpayThtk) return;
      try {
        // Gift mode: the GC- order already exists server-side — just handshake.
        if (giftMode) {
          const hs = await axios.get(`${BASE_URL}/apple-pay-handshake?amount=${giftCheckout.amount}`);
          if (hs.data.status === 200 && hs.data.token) {
            setGpayOrderId(giftCheckout.giftOrderId);
            setGpayThtk(hs.data.token);
          }
          return;
        }
        let orderId = localStorage.getItem("orderId");
        if (orderId) {
          const checkResp = await checkOrderStatus();
          if (!(checkResp?.data && checkResp.data.orderStatus === OrderStatus.NOT_PAID)) {
            localStorage.removeItem("orderId");
            orderId = null;
          }
        }
        if (!orderId) {
          const orderData = await creatOrder({
            ...selectedAddress,
            uid: localStorage.getItem("uniqueUserId"),
            finalAmount: amountCharged,
            orderStatus: OrderStatus.NOT_PAID,
            imagesPayed: imageCount + imagesBlob.length,
            frameType: currentFrameType
          });
          if (!orderData?.oid) return;
          orderId = orderData.oid;
          localStorage.setItem("orderId", orderId);
        }
        const handshakeResponse = await axios.get(`${BASE_URL}/apple-pay-handshake?amount=${amountCharged}`);
        if (handshakeResponse.data.status === 200 && handshakeResponse.data.token) {
          setGpayOrderId(orderId);
          setGpayThtk(handshakeResponse.data.token);
        }
      } catch (e) {
        console.error("SelectCard: Google Pay preload failed", e);
      }
    };
    preload();
  }, [googlePayEligible, selectedAddress, amountCharged, imageCount, imagesBlob.length, currentFrameType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Google Pay charge — synchronous from the tap; on success marks the order paid +
  // navigates to the success page.
  const handleGooglePayment = () => {
    if (!gpayOrderId || !gpayThtk) return;
    setIsLoading(true);
    const framesCount = giftMode ? giftCheckout.frames : imageCount + imagesBlob.length;
    const frameLabel = GPAY_FRAME_LABELS[currentFrameType] || GPAY_FRAME_LABELS.classic;
    const invoiceProductDesc = giftMode
      ? `גיפט קארד בלנדס ×${framesCount}`
      : `תמונות ממוסגרות – ${frameLabel} ×${framesCount}`;
    chargeGooglePay({
      terminal_name: "blends",
      response_language: 'english',
      currency_code: "ILS",
      amount: amountCharged,
      tran_mode: "A",
      thtk: gpayThtk,
      pdesc: invoiceProductDesc,
      contact: giftMode ? giftCheckout.buyerName : selectedAddress.fullName,
      email: (giftMode ? giftCheckout.buyerEmail : selectedAddress.email) || "",
      phone: giftMode ? giftCheckout.buyerPhone : selectedAddress.phoneNumber,
      address: giftMode ? "" : selectedAddress.address,
      city: giftMode ? "" : selectedAddress.city,
      uniqueid: gpayOrderId,
      notify_url: `${window.location.origin}/api/google-pay-webhook?orderId=${gpayOrderId}&method=google_pay`,
    }, (err, response) => {
      setIsLoading(false);
      if (err) { console.error("SelectCard: Google Pay error", err); return; }
      const isSuccess = response?.transaction_response?.success === true || response?.status === "ok";
      const last4Digits = response?.transaction_response?.credit_card_last_4_digits;
      if (isSuccess) {
        if (giftMode) {
          // The webhook GC- branch activates the card; just route to success.
          localStorage.removeItem("orderId");
          navigate(`/gift-success/${gpayOrderId}`);
          return;
        }
        axios.post(`${BASE_URL}/user/changeorderstatus/${gpayOrderId}`, {
          status: OrderStatus.PAID,
          amountCharged: amountCharged,
          last4Digits: last4Digits,
          paymentMethod: 'google_pay'
        }).catch(e => console.error("Error updating order status:", e));
        const oid = gpayOrderId;
        localStorage.clear();
        navigate(`/payment-success/${oid}`);
      }
    });
  };

  return (
    <div className="add-card-modal" style={{...style}}>
      <button className="bitpayment" onClick={() => handleBitPayment()} disabled={isLoading}>
        שלמו בביט
        <img src="/assets/images/bitcartdark.svg" alt="bitpayment" />
      </button>
      <ul>
        <button onClick={() => handleOpenNewCardForm()}>
          כרטיס אשראי
          <img src="/assets/images/card-box-pos.svg" alt="בדיקת כרטיס אשראי" />
        </button>
      </ul>
      {googlePayEligible && (
        <ul>
          <button className="gpayment" onClick={() => handleGooglePayment()} disabled={isLoading || !gpayThtk}>
            שלמו באמצעות Google Pay
            <img src="https://www.gstatic.com/instantbuy/svg/dark_gpay.svg" alt="Google Pay" />
          </button>
        </ul>
      )}
      {/* <ul>
        <button onClick={() => handleOpenNewCardForm(false)}>
          כרטיס דביט\דיירקט
          <img src="/assets/images/credit-pos.svg" alt="תשלום בדביט דיירקט" />
        </button>
      </ul> */}
    </div>
  );
};

export default SelectCardModal;
